import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Search, ArrowRight, Coins, ArrowUp, ArrowDown, AlertCircle, Columns3 } from "lucide-react";
import { formatEuro, formatDate, relativeDays } from "../lib/format";
import DebtForm from "../components/DebtForm";
import PaymentDialog from "../components/PaymentDialog";
import StatusBadge from "../components/StatusBadge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const ALL_COLUMNS = [
  { key: "code", label: "Code", align: "left", required: true },
  { key: "owed_to", label: "Creditor / Purpose", align: "left", required: true },
  { key: "who", label: "Who", align: "left" },
  { key: "status", label: "Status", align: "left" },
  { key: "initial_plus_extra", label: "Initial + Extra", align: "right" },
  { key: "total_paid", label: "Paid", align: "right" },
  { key: "debt_left", label: "Left", align: "right" },
  { key: "monthly", label: "Monthly /mo", align: "right" },
  { key: "due", label: "Next due", align: "right" },
  { key: "payoff", label: "Payoff", align: "right" },
  { key: "days_since_update", label: "Last update", align: "right" },
];

const DEFAULT_VISIBLE = {
  code: true, owed_to: true, who: true, status: true,
  initial_plus_extra: false, total_paid: true, debt_left: true,
  monthly: true, due: true, payoff: false, days_since_update: true,
};
const COLUMNS_STORAGE_KEY = "dt_debts_columns_v1";

export default function Debts() {
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [addOpen, setAddOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [sortBy, setSortBy] = useState("debt_left");
  const [sortDir, setSortDir] = useState("desc");
  const [visible, setVisible] = useState(() => {
    try {
      const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (stored) return { ...DEFAULT_VISIBLE, ...JSON.parse(stored) };
    } catch (err) {
      // localStorage quota / private-mode / SecurityError — fall back to defaults.
      console.debug("[Debts] Could not read column prefs:", err?.message);
    }
    return DEFAULT_VISIBLE;
  });

  useEffect(() => {
    try { localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visible)); } catch (err) {
      console.debug("[Debts] Could not persist column prefs:", err?.message);
    }
  }, [visible]);

  const toggleColumn = (key) => {
    setVisible((v) => ({ ...v, [key]: !v[key] }));
  };
  const resetColumns = () => setVisible(DEFAULT_VISIBLE);

  const isVisible = (key) => {
    const col = ALL_COLUMNS.find((c) => c.key === key);
    return col?.required ? true : !!visible[key];
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/debts");
      setDebts(data.debts || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      // Sensible defaults: text ascending, numbers descending
      const numeric = ["initial_plus_extra", "total_paid", "debt_left", "monthly", "days_since_update", "due"];
      setSortDir(numeric.includes(key) ? "desc" : "asc");
    }
  };

  const filtered = useMemo(() => {
    let rows = debts.filter((d) => {
      // Filter tabs
      if (statusFilter === "open") {
        if (d.status !== "serviced" && d.status !== "not_serviced") return false;
      } else if (statusFilter === "all") {
        // include everything, no status filter
      } else if (d.status !== statusFilter) {
        return false;
      }
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (
        (d.code || "").toLowerCase().includes(s) ||
        (d.owed_to || "").toLowerCase().includes(s) ||
        (d.who || "").toLowerCase().includes(s) ||
        (d.why || "").toLowerCase().includes(s)
      );
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const get = (d) => {
      switch (sortBy) {
        case "code": return (d.code || "").toLowerCase();
        case "owed_to": return (d.owed_to || "").toLowerCase();
        case "who": return (d.who || "").toLowerCase();
        case "status": return d.status || "";
        case "initial_plus_extra":
          return (d.initial_debt || 0) + (d.extra_charges || 0);
        case "total_paid": return d.total_paid || 0;
        case "debt_left": return d.debt_left || 0;
        case "monthly": return d.effective_installment ?? d.monthly_installment ?? 0;
        case "due": return d.effective_next_payment_date || "9999-99-99";
        case "payoff": return d.projected_payoff_date || "9999-99";
        case "days_since_update": return d.days_since_update ?? -1;
        default: return 0;
      }
    };
    rows.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return rows;
  }, [debts, statusFilter, q, sortBy, sortDir]);

  const counts = debts.reduce(
    (acc, d) => {
      acc.all += 1;
      acc[d.status] = (acc[d.status] || 0) + 1;
      if (d.status === "serviced" || d.status === "not_serviced") acc.open += 1;
      return acc;
    },
    { all: 0, open: 0, serviced: 0, not_serviced: 0, paid_off: 0, consolidated: 0 }
  );

  return (
    <div className="space-y-8 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="label-kicker">Ledger</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
            All debts
          </h1>
          <p className="text-[#5C635A] mt-2">
            Click a row to record payments, add interest and see the projected payoff.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white h-11 px-5 self-start sm:self-auto transition-all hover:-translate-y-0.5"
          data-testid="debts-add-debt"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add debt
        </Button>
      </div>

      <div className="card-soft p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList className="bg-[#EEEBE2] flex-wrap h-auto" data-testid="debts-status-filter">
              <TabsTrigger value="open" className="data-[state=active]:bg-white data-[state=active]:text-[#2C4A3B]" data-testid="filter-open">
                Open <span className="ml-1.5 text-[10px] text-[#8B9389] num">{counts.open}</span>
              </TabsTrigger>
              <TabsTrigger value="not_serviced" className="data-[state=active]:bg-white data-[state=active]:text-[#B04A32]" data-testid="filter-not-serviced">
                Overdue <span className="ml-1.5 text-[10px] text-[#8B9389] num">{counts.not_serviced || 0}</span>
              </TabsTrigger>
              <TabsTrigger value="paid_off" className="data-[state=active]:bg-white data-[state=active]:text-[#1D3328]" data-testid="filter-paid-off">
                Paid off <span className="ml-1.5 text-[10px] text-[#8B9389] num">{counts.paid_off || 0}</span>
              </TabsTrigger>
              <TabsTrigger value="consolidated" className="data-[state=active]:bg-white data-[state=active]:text-[#4B4478]" data-testid="filter-consolidated">
                Consolidated <span className="ml-1.5 text-[10px] text-[#8B9389] num">{counts.consolidated || 0}</span>
              </TabsTrigger>
              <TabsTrigger value="all" className="data-[state=active]:bg-white data-[state=active]:text-[#1D3328]" data-testid="filter-all">
                All <span className="ml-1.5 text-[10px] text-[#8B9389] num">{counts.all}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 max-w-md ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B9389]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by code, creditor, person…"
              className="pl-9 rounded-xl border-[#E5E3DB] bg-white"
              data-testid="debts-search-input"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl border-[#E5E3DB] hover:bg-[#EEEBE2] text-[#1D3328]"
                data-testid="debts-columns-button"
              >
                <Columns3 className="w-4 h-4 mr-1.5" strokeWidth={1.7} />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border-[#E5E3DB]">
              <DropdownMenuLabel className="text-[10px] tracking-[0.22em] uppercase text-[#8B9389]">
                Show columns
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {ALL_COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={isVisible(col.key)}
                  disabled={col.required}
                  onCheckedChange={() => !col.required && toggleColumn(col.key)}
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`col-toggle-${col.key}`}
                >
                  {col.label}
                  {col.required && (
                    <span className="ml-auto text-[10px] text-[#8B9389]">always</span>
                  )}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <button
                type="button"
                onClick={resetColumns}
                className="w-full text-left text-xs text-[#5C635A] hover:text-[#1D3328] hover:bg-[#EEEBE2] rounded-sm px-2 py-1.5"
                data-testid="col-reset"
              >
                Reset to defaults
              </button>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {loading ? (
          <div className="text-[#8B9389] tracking-[0.2em] uppercase text-xs py-8 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[#5C635A]">
              {debts.length === 0 ? "No debts yet. Add your first one." : "No matches for your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm" data-testid="debts-table">
              <thead>
                <tr className="text-left label-kicker border-b border-[#EEEBE2]">
                  {isVisible("code") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="code" onClick={toggleSort}>Code</SortableTh>}
                  {isVisible("owed_to") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="owed_to" onClick={toggleSort}>Creditor / Purpose</SortableTh>}
                  {isVisible("who") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="who" onClick={toggleSort}>Who</SortableTh>}
                  {isVisible("status") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="status" onClick={toggleSort}>Status</SortableTh>}
                  {isVisible("initial_plus_extra") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="initial_plus_extra" onClick={toggleSort} align="right">Initial + Extra</SortableTh>}
                  {isVisible("total_paid") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="total_paid" onClick={toggleSort} align="right">Paid</SortableTh>}
                  {isVisible("debt_left") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="debt_left" onClick={toggleSort} align="right">Left</SortableTh>}
                  {isVisible("monthly") && (
                    <SortableTh sortBy={sortBy} sortDir={sortDir} k="monthly" onClick={toggleSort} align="right">
                      Monthly /mo
                    </SortableTh>
                  )}
                  {isVisible("due") && (
                    <SortableTh sortBy={sortBy} sortDir={sortDir} k="due" onClick={toggleSort} align="right">
                      Next due
                    </SortableTh>
                  )}
                  {isVisible("payoff") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="payoff" onClick={toggleSort} align="right">Payoff</SortableTh>}
                  {isVisible("days_since_update") && <SortableTh sortBy={sortBy} sortDir={sortDir} k="days_since_update" onClick={toggleSort} align="right">Last update</SortableTh>}
                  <th className="py-3 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const gross = (d.initial_debt || 0) + (d.extra_charges || 0) + (d.interest_added || 0);
                  const pct = gross > 0 ? Math.min(100, Math.round((d.total_paid / gross) * 100)) : 0;
                  const monthlyAmt = d.effective_installment ?? d.monthly_installment;
                  const dueDate = d.effective_next_payment_date;
                  const stale = d.needs_refresh;
                  return (
                    <tr
                      key={d.id}
                      className="border-b border-[#EEEBE2] hover:bg-[#FAF8F2]/60 transition-colors"
                      data-testid="debt-row"
                    >
                      {isVisible("code") && (
                        <td className="py-3 px-2 font-mono text-xs text-[#5C635A] whitespace-nowrap">{d.code}</td>
                      )}
                      {isVisible("owed_to") && (
                        <td className="py-3 px-2">
                          <Link to={`/debts/${d.id}`} className="block">
                            <div className="font-medium text-[#1D3328]">{d.owed_to}</div>
                            <div className="text-xs text-[#8B9389] line-clamp-1 max-w-sm">{d.why}</div>
                            <div className="mt-2 flex items-center gap-2">
                              <Progress value={pct} className="h-1.5 w-32 bg-[#EEEBE2]" />
                              <span className="text-[10px] text-[#8B9389] num">{pct}%</span>
                            </div>
                          </Link>
                        </td>
                      )}
                      {isVisible("who") && <td className="py-3 px-2 text-[#5C635A]">{d.who}</td>}
                      {isVisible("status") && <td className="py-3 px-2"><StatusBadge status={d.status} /></td>}
                      {isVisible("initial_plus_extra") && (
                        <td className="py-3 px-2 text-right num text-[#5C635A]">
                          {formatEuro((d.initial_debt || 0) + (d.extra_charges || 0))}
                        </td>
                      )}
                      {isVisible("total_paid") && (
                        <td className="py-3 px-2 text-right num text-[#2C4A3B]">{formatEuro(d.total_paid)}</td>
                      )}
                      {isVisible("debt_left") && (
                        <td className="py-3 px-2 text-right num font-medium text-[#E07A5F]">
                          {formatEuro(d.debt_left)}
                        </td>
                      )}
                      {isVisible("monthly") && (
                        <td className="py-3 px-2 text-right num text-[#1D3328]">
                          {formatEuro(monthlyAmt)}
                        </td>
                      )}
                      {isVisible("due") && (
                        <td className="py-3 px-2 text-right text-xs whitespace-nowrap">
                          {dueDate ? (
                            <span className={d.status === "not_serviced" ? "text-[#B04A32] font-medium" : "text-[#5C635A]"}>
                              {formatDate(dueDate)}
                            </span>
                          ) : (
                            <span className="text-[#8B9389]">—</span>
                          )}
                        </td>
                      )}
                      {isVisible("payoff") && (
                        <td className="py-3 px-2 text-right text-[#5C635A] whitespace-nowrap">
                          {d.debt_left <= 0
                            ? "Paid off"
                            : d.projected_payoff_date
                            ? formatDate(d.projected_payoff_date)
                            : "—"}
                        </td>
                      )}
                      {isVisible("days_since_update") && (
                        <td className="py-3 px-2 text-right whitespace-nowrap" data-testid="debt-last-update">
                          {stale ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-md bg-[#FAEEE5] border border-[#E07A5F]/40 text-[#B04A32] text-[11px] font-medium px-2 py-0.5"
                              title={`No activity for ${d.days_since_update} days — please refresh this debt.`}
                            >
                              <AlertCircle className="w-3 h-3" />
                              {relativeDays(d.days_since_update)}
                            </span>
                          ) : (
                            <span className="text-xs text-[#5C635A]">
                              {relativeDays(d.days_since_update)}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => { e.preventDefault(); setPayTarget(d); }}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#EEEBE2] hover:bg-[#E5DCC5] text-[#1D3328] text-xs font-medium px-2.5 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#EEEBE2]"
                            data-testid="debt-quick-pay"
                            disabled={d.debt_left <= 0}
                            title={d.debt_left <= 0 ? "Paid off" : "Record payment"}
                          >
                            <Coins className="w-3.5 h-3.5" /> Pay
                          </button>
                          <Link
                            to={`/debts/${d.id}`}
                            className="inline-flex items-center gap-1 text-[#2C4A3B] hover:text-[#1D3328] text-xs font-medium"
                            data-testid="debt-open-link"
                          >
                            Open <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DebtForm open={addOpen} onOpenChange={setAddOpen} onSaved={() => load()} />
      <PaymentDialog
        open={Boolean(payTarget)}
        onOpenChange={(v) => { if (!v) setPayTarget(null); }}
        debt={payTarget}
        onSaved={() => load()}
      />
    </div>
  );
}

function SortableTh({ children, sortBy, sortDir, k, onClick, align = "left" }) {
  const active = sortBy === k;
  const Icon = sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`py-3 px-2 ${align === "right" ? "text-right" : "text-left"} cursor-pointer select-none group whitespace-nowrap`}
      onClick={() => onClick(k)}
      data-testid={`sort-${k}`}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span
        className={`inline-flex items-center gap-1 transition-colors ${
          active ? "text-[#1D3328]" : "text-[#8B9389] group-hover:text-[#5C635A]"
        }`}
      >
        {children}
        {active ? (
          <Icon className="w-3 h-3" strokeWidth={2.2} />
        ) : (
          <ArrowDown className="w-3 h-3 opacity-0 group-hover:opacity-30" strokeWidth={2} />
        )}
      </span>
    </th>
  );
}
